import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  NotificationEvent,
  NotificationEventInput,
  NotificationType,
  ToastPosition,
  ToastVariant,
} from "./notification-event";
import { notificationToastPosition } from "./notification-event";
import {
  dismissNotificationEvent,
  dismissTransientNotificationEvents,
  upsertNotificationEvent,
  visibleNotificationEvents,
} from "./notification-event-store";
const DND_STORAGE_KEY = "fintheon:notification-blocklist";
const API_ERROR_CASCADE_DESCRIPTION =
  "Multiple backend errors are being grouped into this one notice.";

export interface LegacyToast {
  id: string;
  message: string;
  variant: ToastVariant;
  description?: string;
  notificationType?: NotificationType;
  position?: ToastPosition;
  cta?: { label: string; onClick: () => void };
  secondaryCta?: { label: string; onClick: () => void };
  durationMs?: number;
  leadingGlyph?: string;
  exiting?: boolean;
}
interface UnifiedNotificationContextValue {
  events: NotificationEvent[];
  visibleEvents: NotificationEvent[];
  toasts: LegacyToast[];
  publishEvent: (input: NotificationEventInput) => string;
  addToast: (
    message: string,
    variant?: ToastVariant,
    description?: string,
    notificationType?: NotificationType,
    position?: ToastPosition,
    cta?: { label: string; onClick: () => void },
    secondaryCta?: { label: string; onClick: () => void },
    durationMs?: number,
    leadingGlyph?: string,
  ) => string;
  dismissToast: (id: string) => void;
  dismissAllTransient: () => void;
  markEventRead: (id: string) => void;
  blockNotificationType: (type: NotificationType) => void;
  isNotificationBlocked: (type: NotificationType) => boolean;
  resetBlockedNotifications: () => void;
  blockedTypes: NotificationType[];
  showMaintenanceToast: (onUpdate: () => void) => string;
}

const UnifiedNotificationContext =
  createContext<UnifiedNotificationContextValue | null>(null);
function loadBlocklist(): NotificationType[] {
  try {
    const raw = localStorage.getItem(DND_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NotificationType[]) : [];
  } catch {
    return [];
  }
}
function saveBlocklist(types: NotificationType[]) {
  try {
    localStorage.setItem(DND_STORAGE_KEY, JSON.stringify(types));
  } catch {
    // Storage can be unavailable in hardened/private sessions.
  }
}
function nextEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `notice-${crypto.randomUUID()}`;
  }
  return `notice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
function legacyDuration(variant: ToastVariant, hasAction: boolean) {
  if (variant === "updating" || hasAction) return null;
  if (variant === "reminder") return 8_000;
  if (variant === "watch" || variant === "risk-signal") return 12_000;
  if (variant === "vix") return 10_000;
  if (variant === "error") return 2_500;
  return 4_000;
}
function legacySeverity(variant: ToastVariant) {
  if (variant === "success") return "success" as const;
  if (variant === "error") return "error" as const;
  if (variant === "updating") return "loading" as const;
  if (variant === "reminder" || variant === "vix") return "warning" as const;
  return "info" as const;
}

export function UnifiedToastProvider({
  children,
  mobile = false,
  suppressToasts = false,
}: {
  children: ReactNode;
  mobile?: boolean;
  suppressToasts?: boolean;
}) {
  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const eventsRef = useRef(events);
  const [blockedTypes, setBlockedTypes] =
    useState<NotificationType[]>(loadBlocklist);
  const commit = useCallback((next: NotificationEvent[]) => {
    eventsRef.current = next;
    setEvents(next);
  }, []);
  const publishEvent = useCallback(
    (input: NotificationEventInput) => {
      if (suppressToasts) return "";
      if (mobile && input.notificationType !== "api-error") return "";
      if (
        input.notificationType &&
        blockedTypes.includes(input.notificationType)
      ) {
        return "";
      }
      const lane = notificationToastPosition(input);
      const result = upsertNotificationEvent(
        eventsRef.current,
        { ...input, legacyPosition: lane },
        Date.now(),
        nextEventId,
      );
      commit(result.events);
      return result.id;
    },
    [blockedTypes, commit, mobile, suppressToasts],
  );
  const dismissToast = useCallback(
    (id: string) =>
      commit(dismissNotificationEvent(eventsRef.current, id, Date.now())),
    [commit],
  );
  const dismissAllTransient = useCallback(
    () =>
      commit(dismissTransientNotificationEvents(eventsRef.current, Date.now())),
    [commit],
  );
  const markEventRead = useCallback(
    (id: string) =>
      commit(
        eventsRef.current.map((event) =>
          event.id === id ? { ...event, readAt: Date.now() } : event,
        ),
      ),
    [commit],
  );

  const addToast: UnifiedNotificationContextValue["addToast"] = useCallback(
    (
      message,
      variant = "info",
      description,
      notificationType,
      _position,
      cta,
      secondaryCta,
      durationMs,
      leadingGlyph,
    ) =>
      publishEvent({
        source: `app:${notificationType ?? "general"}`,
        kind: notificationType ?? variant,
        severity: legacySeverity(variant),
        title: message,
        body:
          notificationType === "api-error" && !description
            ? API_ERROR_CASCADE_DESCRIPTION
            : description,
        dedupeKey:
          notificationType === "api-error" ? "toast:api-error" : undefined,
        notificationType,
        legacyVariant: variant,
        legacyPosition: notificationToastPosition({
          source: `app:${notificationType ?? "general"}`,
          kind: notificationType ?? variant,
          notificationType,
          legacyVariant: variant,
        }),
        action: cta,
        secondaryAction: secondaryCta,
        durationMs: durationMs ?? legacyDuration(variant, Boolean(cta)),
        persistence: cta || variant === "updating" ? "persistent" : "transient",
        leadingGlyph,
      }),
    [publishEvent],
  );

  useEffect(() => {
    const timers = visibleNotificationEvents(events)
      .filter((event) => event.expiresAt !== null)
      .map((event) =>
        window.setTimeout(
          () => dismissToast(event.id),
          Math.max(0, (event.expiresAt ?? Date.now()) - Date.now()),
        ),
      );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [dismissToast, events]);

  const visibleEvents = useMemo(
    () => (suppressToasts ? [] : visibleNotificationEvents(events)),
    [events, suppressToasts],
  );
  const toasts = useMemo(
    () =>
      visibleEvents.map<LegacyToast>((event) => ({
        id: event.id,
        message: event.title,
        description: event.body,
        variant: event.legacyVariant ?? "info",
        notificationType: event.notificationType,
        position: event.legacyPosition ?? "bottom-left",
        cta: event.action,
        secondaryCta: event.secondaryAction,
        leadingGlyph: event.leadingGlyph,
      })),
    [visibleEvents],
  );

  const blockNotificationType = useCallback((type: NotificationType) => {
    setBlockedTypes((current) => {
      const next = current.includes(type) ? current : [...current, type];
      saveBlocklist(next);
      return next;
    });
  }, []);
  const resetBlockedNotifications = useCallback(() => {
    setBlockedTypes([]);
    saveBlocklist([]);
  }, []);
  const isNotificationBlocked = useCallback(
    (type: NotificationType) => blockedTypes.includes(type),
    [blockedTypes],
  );
  const showMaintenanceToast = useCallback(
    (onUpdate: () => void) =>
      addToast(
        "Fintheon is updating...",
        "updating",
        undefined,
        "system-update",
        "bottom-left",
        { label: "Update App", onClick: onUpdate },
      ),
    [addToast],
  );
  const value = useMemo(
    () => ({
      events,
      visibleEvents,
      toasts,
      publishEvent,
      addToast,
      dismissToast,
      dismissAllTransient,
      markEventRead,
      blockNotificationType,
      isNotificationBlocked,
      resetBlockedNotifications,
      blockedTypes,
      showMaintenanceToast,
    }),
    [
      addToast,
      blockNotificationType,
      blockedTypes,
      dismissAllTransient,
      dismissToast,
      events,
      isNotificationBlocked,
      markEventRead,
      publishEvent,
      resetBlockedNotifications,
      showMaintenanceToast,
      toasts,
      visibleEvents,
    ],
  );
  return (
    <UnifiedNotificationContext.Provider value={value}>
      {children}
    </UnifiedNotificationContext.Provider>
  );
}
export function useUnifiedToast() {
  const context = useContext(UnifiedNotificationContext);
  if (!context) throw new Error("useToast must be used inside ToastProvider");
  return context;
}
